import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ShieldAlert, X } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Banner informativo (non bloccante) su riservatezza e NDA: l'utente conferma
 * con la spunta "ho letto e accetto" e l'accettazione viene registrata lato server.
 */
export const NdaBanner = () => {
  const [stato, setStato] = useState(null);
  const [spuntato, setSpuntato] = useState(false);
  const [chiuso, setChiuso] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/nda").then(({ data }) => setStato(data)).catch(() => setStato(null));
  }, []);

  if (!stato?.documento || stato.accettata || chiuso) return null;

  const apri = async () => {
    try {
      await downloadFile(`/documents/${stato.documento.id}/download`, stato.documento.filename);
    } catch {
      toast.error("Non riesco ad aprire il documento");
    }
  };

  const accetta = async () => {
    setBusy(true);
    try {
      await api.post("/nda/accetta");
      setStato({ ...stato, accettata: true });
      toast.success("Accettazione registrata: grazie");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9000] border-t border-amber-200 bg-amber-50/95 backdrop-blur"
         data-testid="nda-banner">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-3.5 sm:px-6 lg:flex-row lg:items-center lg:gap-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Informazioni riservate e NDA</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
              I contenuti di questa applicazione, analisi funzionale e analisi tecnica incluse, sono
              riservati e coperti da accordo di non divulgazione. Ogni accesso ai documenti viene
              registrato. Leggi «{stato.documento.titolo}» e conferma la presa visione.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:shrink-0">
          <Button variant="outline" onClick={apri}
                  className="h-9 border-amber-300 bg-white text-xs"
                  data-testid="nda-open-document">
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Apri il documento
          </Button>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
            <Checkbox checked={spuntato} onCheckedChange={(v) => setSpuntato(!!v)}
                      data-testid="nda-checkbox" />
            Ho letto e accetto
          </label>
          <Button onClick={accetta} disabled={!spuntato || busy}
                  className="h-9 bg-slate-900 text-xs text-white hover:bg-slate-800"
                  data-testid="nda-accept-button">
            Conferma
          </Button>
          <button onClick={() => setChiuso(true)} title="Chiudi per ora"
                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-amber-100 hover:text-slate-700"
                  data-testid="nda-dismiss-button">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NdaBanner;
